import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Sidebar } from './components/layout/Sidebar';
import { Topbar } from './components/layout/Topbar';
import { AIPanel } from './components/layout/AIPanel';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useUIStore } from './stores/ui';
import { useTenantStore } from './stores/tenant';
import { useAuthStore } from './stores/auth';
import { tenantsApi } from './api/tenants';
import { authApi } from './api/auth';
import styles from './app.module.css';

const Dashboard = lazy(() => import('./pages/dashboard/Dashboard').then(m => ({ default: m.Dashboard })));
const Tenants = lazy(() => import('./pages/tenants/Tenants').then(m => ({ default: m.Tenants })));
const Users = lazy(() => import('./pages/identity/users/Users').then(m => ({ default: m.Users })));
const Groups = lazy(() => import('./pages/identity/groups/Groups').then(m => ({ default: m.Groups })));
const ConditionalAccess = lazy(() => import('./pages/identity/conditionalAccess/ConditionalAccess').then(m => ({ default: m.ConditionalAccess })));
const IdentityDevices = lazy(() => import('./pages/identity/devices/Devices').then(m => ({ default: m.Devices })));
const Roles = lazy(() => import('./pages/identity/roles/Roles').then(m => ({ default: m.Roles })));
const SecureScore = lazy(() => import('./pages/security/SecureScore').then(m => ({ default: m.SecureScore })));
const MFAStatus = lazy(() => import('./pages/security/MFAStatus').then(m => ({ default: m.MFAStatus })));
const Defender = lazy(() => import('./pages/security/Defender').then(m => ({ default: m.Defender })));
const RiskyUsers = lazy(() => import('./pages/security/RiskyUsers').then(m => ({ default: m.RiskyUsers })));
const SecurityCompliance = lazy(() => import('./pages/security/Compliance').then(m => ({ default: m.Compliance })));
const Mailboxes = lazy(() => import('./pages/exchange/Mailboxes').then(m => ({ default: m.Mailboxes })));
const SharedMailboxes = lazy(() => import('./pages/exchange/SharedMailboxes').then(m => ({ default: m.SharedMailboxes })));
const MailHardening = lazy(() => import('./pages/exchange/MailHardening').then(m => ({ default: m.MailHardening })));
const MessageTrace = lazy(() => import('./pages/exchange/MessageTrace').then(m => ({ default: m.MessageTrace })));
const EmailRemediation = lazy(() => import('./pages/exchange/EmailRemediation').then(m => ({ default: m.EmailRemediation })));
const EmailJournaling = lazy(() => import('./pages/exchange/EmailJournaling').then(m => ({ default: m.EmailJournaling })));
const DistributionGroups = lazy(() => import('./pages/exchange/DistributionGroups').then(m => ({ default: m.DistributionGroups })));
const MailFlow = lazy(() => import('./pages/exchange/MailFlow').then(m => ({ default: m.MailFlow })));
const SpamPolicies = lazy(() => import('./pages/exchange/SpamPolicies').then(m => ({ default: m.SpamPolicies })));
const TeamsPolicies = lazy(() => import('./pages/teams/TeamsPolicies').then(m => ({ default: m.TeamsPolicies })));
const SharePoint = lazy(() => import('./pages/teams/SharePoint').then(m => ({ default: m.SharePoint })));
const OneDrive = lazy(() => import('./pages/teams/OneDrive').then(m => ({ default: m.OneDrive })));
const IntuneDevices = lazy(() => import('./pages/intune/IntuneDevices').then(m => ({ default: m.IntuneDevices })));
const ConfigProfiles = lazy(() => import('./pages/intune/ConfigProfiles').then(m => ({ default: m.ConfigProfiles })));
const IntuneCompliance = lazy(() => import('./pages/intune/IntuneCompliance').then(m => ({ default: m.IntuneCompliance })));
const IntuneApps = lazy(() => import('./pages/intune/IntuneApps').then(m => ({ default: m.IntuneApps })));
const IntuneScripts = lazy(() => import('./pages/intune/IntuneScripts').then(m => ({ default: m.IntuneScripts })));
const Standards = lazy(() => import('./pages/standards/Standards').then(m => ({ default: m.Standards })));
const UniFiWrapper = lazy(() => import('./pages/unifi/UniFiWrapper').then(m => ({ default: m.UniFiWrapper })));
const AIConfigurations = lazy(() => import('./pages/ai/AIConfigurations').then(m => ({ default: m.AIConfigurations })));
const AIHistory = lazy(() => import('./pages/ai/AIHistory').then(m => ({ default: m.AIHistory })));
const AIChat = lazy(() => import('./pages/ai/AIChat').then(m => ({ default: m.AIChat })));
const DomainTools = lazy(() => import('./pages/tools/DomainTools').then(m => ({ default: m.DomainTools })));
const ReportBuilder = lazy(() => import('./pages/tools/ReportBuilder').then(m => ({ default: m.ReportBuilder })));
const TemplateLibrary = lazy(() => import('./pages/tools/TemplateLibrary').then(m => ({ default: m.TemplateLibrary })));
const Scheduler = lazy(() => import('./pages/tools/Scheduler').then(m => ({ default: m.Scheduler })));
const Webhooks = lazy(() => import('./pages/webhooks/Webhooks').then(m => ({ default: m.Webhooks })));
const EnqueIntegration = lazy(() => import('./pages/integrations/EnqueIntegration').then(m => ({ default: m.EnqueIntegration })));
const Agencies = lazy(() => import('./pages/agencies/Agencies').then(m => ({ default: m.Agencies })));
const OnboardingGdap = lazy(() => import('./pages/onboarding/OnboardingGdap').then(m => ({ default: m.OnboardingGdap })));
const Settings = lazy(() => import('./pages/settings/Settings').then(m => ({ default: m.Settings })));
const NotFound = lazy(() => import('./pages/NotFound').then(m => ({ default: m.NotFound })));
const Login = lazy(() => import('./pages/auth/Login').then(m => ({ default: m.Login })));

function PageLoader() {
  return (
    <div className={styles.pageLoader}>
      <div className={styles.spinner} />
    </div>
  );
}

function AppShell() {
  const theme = useUIStore(s => s.theme);
  const { tenants, setTenants } = useTenantStore();
  const setCurrentUser = useAuthStore(s => s.setCurrentUser);
  const setAuthLoaded = useAuthStore(s => s.setLoaded);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    authApi.me()
      .then(setCurrentUser)
      .catch(() => setCurrentUser(null))
      .finally(() => setAuthLoaded(true));
  }, [setCurrentUser, setAuthLoaded]);

  useEffect(() => {
    tenantsApi.list().then(setTenants).catch(console.error);
  }, [setTenants]);

  return (
    <div className={styles.shell}>
      <Sidebar />
      <div className={styles.main}>
        <Topbar tenants={tenants} />
        <main className={styles.content}>
          <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="onboarding/gdap" element={<OnboardingGdap />} />
              <Route path="tenants" element={<Tenants />} />
              <Route path="identity">
                <Route index element={<Navigate to="users" replace />} />
                <Route path="users" element={<Users />} />
                <Route path="groups" element={<Groups />} />
                <Route path="devices" element={<IdentityDevices />} />
                <Route path="roles" element={<Roles />} />
                <Route path="conditional-access" element={<ConditionalAccess />} />
                <Route path="risky-users" element={<RiskyUsers />} />
              </Route>
              <Route path="security">
                <Route index element={<Navigate to="secure-score" replace />} />
                <Route path="secure-score" element={<SecureScore />} />
                <Route path="mfa" element={<MFAStatus />} />
                <Route path="risky-users" element={<RiskyUsers />} />
                <Route path="defender" element={<Defender />} />
                <Route path="compliance" element={<SecurityCompliance />} />
              </Route>
              <Route path="standards" element={<Standards />} />
              <Route path="intune">
                <Route index element={<Navigate to="devices" replace />} />
                <Route path="devices" element={<IntuneDevices />} />
                <Route path="profiles" element={<ConfigProfiles />} />
                <Route path="compliance" element={<IntuneCompliance />} />
                <Route path="apps" element={<IntuneApps />} />
                <Route path="scripts" element={<IntuneScripts />} />
              </Route>
              <Route path="exchange">
                <Route index element={<Navigate to="mailboxes" replace />} />
                <Route path="mailboxes" element={<Mailboxes />} />
                <Route path="shared" element={<SharedMailboxes />} />
                <Route path="mail-hardening" element={<MailHardening />} />
                <Route path="message-trace" element={<MessageTrace />} />
                <Route path="remediation" element={<EmailRemediation />} />
                <Route path="journaling" element={<EmailJournaling />} />
                <Route path="distribution" element={<DistributionGroups />} />
                <Route path="mail-flow" element={<MailFlow />} />
                <Route path="spam" element={<SpamPolicies />} />
                <Route path="dkim-dmarc" element={<Navigate to="/exchange/mail-hardening" replace />} />
              </Route>
              <Route path="teams">
                <Route index element={<Navigate to="policies" replace />} />
                <Route path="policies" element={<TeamsPolicies />} />
                <Route path="sharepoint" element={<SharePoint />} />
                <Route path="onedrive" element={<OneDrive />} />
              </Route>
              <Route path="unifi/*" element={<UniFiWrapper />} />
              <Route path="ai">
                <Route index element={<Navigate to="chat" replace />} />
                <Route path="chat" element={<AIChat />} />
                <Route path="configurations" element={<AIConfigurations />} />
                <Route path="history" element={<AIHistory />} />
              </Route>
              <Route path="tools">
                <Route index element={<Navigate to="templates" replace />} />
                <Route path="templates" element={<TemplateLibrary />} />
                <Route path="scheduler" element={<Scheduler />} />
                <Route path="reports" element={<ReportBuilder />} />
                <Route path="domain" element={<DomainTools />} />
              </Route>
              <Route path="webhooks" element={<Webhooks />} />
              <Route path="integrations">
                <Route index element={<Navigate to="enque" replace />} />
                <Route path="enque" element={<EnqueIntegration />} />
              </Route>
              <Route path="agencies" element={<Agencies />} />
              <Route path="settings" element={<Settings />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </ErrorBoundary>
        </main>
      </div>
      <AIPanel />
    </div>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/auth/login"
          element={
            <Suspense fallback={<PageLoader />}>
              <Login />
            </Suspense>
          }
        />
        <Route path="/*" element={<AppShell />} />
      </Routes>
    </BrowserRouter>
  );
}
